import { API_URL, storedToken } from './auth-client.ts';

export interface WordList {
  slug: string;
  title: string;
  description: string | null;
  license: string;
  attribution: string;
  sourceUrl: string | null;
  size: number;
}

export interface MediaCategory {
  slug: string;
  title: string;
  isDefault: boolean;
}

export interface Band {
  band: number;
  from: number;
  to: number;
}

interface Mnemonic {
  text: string;
  categorySlug: string;
  categoryTitle: string;
}

type SenseStatus = 'known' | 'settled' | 'learning' | 'started' | 'new' | 'locked';

export interface QueueCard {
  senseId: number;
  cue: string;
  kind: 'new' | 'due';
  stage: 'new' | 'started' | 'learning' | 'settled' | 'known';
  band: number | null;
  lemma: string | null;
  mnemonic: Mnemonic | null;
  sentence: { text: string; translation: string } | null;
  sense: { index: number; total: number };
  siblings: Array<{ gloss: string; status: SenseStatus; current: boolean }> | null;
  reps: number;
  lapses: number;
  hintDelayMs: number;
}

export type SocialProvider = 'google' | 'apple';

export interface AuthMethods {
  emailAndPassword: true;
  social: SocialProvider[];
}

export type GradeName = 'again' | 'hard' | 'good' | 'easy';

export interface SubmitResult {
  verdict: 'correct' | 'typo' | 'incorrect';
  appliedGrade: GradeName;
  lemma: string;
  due: string;
  scheduledDays: number;
  state: number;
  reps: number;
  lapses: number;
}

export interface Queue {
  cards: QueueCard[];
  dueTotal: number;
  newWithheld: boolean;
  backlogLimit: number;
}

export type PeriodKey = '7d' | '30d' | '90d' | '365d' | 'all';

export interface ProgressOverview {
  list: { slug: string; title: string; total: number; ranked: boolean };
  period: PeriodKey;
  maturityDays: number;
  stages: {
    known: number;
    settled: number;
    inProgress: number;
    justStarted: number;
    notQueued: number;
  };
  started: number;
  band: {
    current: number;
    of: number;
    from: number;
    to: number;
    started: number;
    answerable: number;
    total: number;
  } | null;
  coverage: {
    known: number;
    inProgress: number;
    justStarted: number;
    gained: number;
    ceiling: number;
    reading: { assisted: number; unassisted: number };
  } | null;
  daily: Array<{ date: string; fresh: number; repeats: number }>;
}

export interface BandProgressRow {
  band: number;
  from: number;
  to: number;
  total: number;
  answerable: number;
  share: number | null;
  justStarted: number;
  inProgress: number;
  settled: number;
  learned: number;
}

export interface BandProgress {
  list: { slug: string; title: string };
  maturityDays: number;
  bands: BandProgressRow[];
}

export type WordStatus = 'known' | 'settled' | 'learning' | 'started' | 'new';

export interface SourceDetail {
  slug: string;
  title: string;
  description: string | null;
  license: string;
  attribution: string;
  sourceUrl: string | null;
  bandCount: number;
  bands: BandProgressRow[];
  overlap: Array<{ slug: string; title: string; shared: number; sharedKnown: number }>;
}

export interface SearchWord {
  lemma: string;
  functionWord: boolean;
  status: WordStatus;
  senses: Array<{ gloss: string; status: SenseStatus }>;
  places: Array<{ slug: string; title: string; band: number; level: number; rank: number }>;
}

export interface ListWords {
  list: { slug: string; title: string };
  band?: { from: number; to: number };
  counts: Record<WordStatus | 'all', number>;
  words: Array<{
    wordId: number;
    lemma: string;
    senses: Array<{
      gloss: string;
      example: string | null;
      translation: string | null;
      status: SenseStatus;
    }>;
    functionWord: boolean;
    rank: number;
    band: number;
    status: WordStatus;
    alsoIn: string[];
  }>;
}

export interface Settings {
  newPerDay: number;
  newPerDayMax: number;
  vocabCeiling: number;
}

export interface Attribution {
  sources: Array<{
    title: string;
    license: string;
    url: string | null;
    list: string | null;
  }>;
  vocabCeiling: number;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

const REQUEST_TIMEOUT_MS = 15_000;

function noAnswer(error: unknown): ApiError {
  const timedOut = (error as { name?: string } | null)?.name === 'TimeoutError';
  return new ApiError(
    0,
    timedOut ? 'timeout' : 'network_error',
    timedOut ? 'Сервер не ответил' : 'Нет связи с сервером',
  );
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = storedToken();
  let response: Response;
  try {
    response = await fetch(`${API_URL}${path}`, {
      ...init,
      signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
      credentials: 'include',
      headers: {
        ...(init.body === undefined ? {} : { 'content-type': 'application/json' }),
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...init.headers,
      },
    });
  } catch (error) {
    throw noAnswer(error);
  }

  if (!response.ok) {
    const body = (await response.json().catch(() => ({}))) as {
      error?: string;
      message?: string;
    };
    throw new ApiError(
      response.status,
      body.error ?? 'request_failed',
      body.message ?? response.statusText,
    );
  }

  return (await response.json()) as T;
}

export const api = {
  authMethods: () => request<AuthMethods>('/api/auth-methods'),
  lists: () => request<{ lists: WordList[] }>('/api/lists'),
  categories: () => request<{ categories: MediaCategory[] }>('/api/categories'),
  bands: () => request<{ bands: Band[] }>('/api/bands'),
  attribution: () => request<Attribution>('/api/attribution'),

  source: (slug: string) => request<SourceDetail>(`/api/sources/${encodeURIComponent(slug)}`),

  listWords: (
    slug: string,
    params: { band?: number; status?: WordStatus; q?: string; offset?: number },
  ) => {
    const query = new URLSearchParams();
    if (params.band !== undefined) query.set('band', String(params.band));
    if (params.status) query.set('status', params.status);
    if (params.q) query.set('q', params.q);
    if (params.offset) query.set('offset', String(params.offset));
    return request<ListWords>(`/api/sources/${encodeURIComponent(slug)}/words?${query.toString()}`);
  },
  search: (q: string) => request<{ words: SearchWord[] }>(`/api/search?q=${encodeURIComponent(q)}`),

  settings: () => request<Settings>('/api/settings'),

  saveSettings: (newPerDay: number) =>
    request<Settings>('/api/settings', { method: 'PATCH', body: JSON.stringify({ newPerDay }) }),

  resetProgress: () => request<{ reset: true }>('/api/progress/reset', { method: 'POST' }),

  exportCsv: async (): Promise<Blob> => {
    const token = storedToken();
    let response: Response;
    try {
      response = await fetch(`${API_URL}/api/export.csv`, {
        signal: AbortSignal.timeout(REQUEST_TIMEOUT_MS),
        credentials: 'include',
        headers: token ? { authorization: `Bearer ${token}` } : {},
      });
    } catch (error) {
      throw noAnswer(error);
    }
    if (!response.ok) throw new ApiError(response.status, 'export_failed', response.statusText);
    return response.blob();
  },

  overview: (list: string, period: PeriodKey) =>
    request<ProgressOverview>(
      `/api/progress/overview?list=${encodeURIComponent(list)}&period=${period}`,
    ),

  bandProgress: (list: string) =>
    request<BandProgress>(`/api/progress/bands?list=${encodeURIComponent(list)}`),

  queue: (params: { list: string; category?: string; due?: number; new?: number }) => {
    const query = new URLSearchParams({ list: params.list });
    if (params.category) query.set('category', params.category);
    if (params.due !== undefined) query.set('due', String(params.due));
    if (params.new !== undefined) query.set('new', String(params.new));
    return request<Queue>(`/api/review/queue?${query.toString()}`);
  },

  submit: (
    senseId: number,
    body: {
      answer: string;
      lettersShown?: number;
      durationMs?: number;
      attemptId?: string;
      attempts?: number;
    },
  ) =>
    request<SubmitResult>(`/api/review/${senseId}`, { method: 'POST', body: JSON.stringify(body) }),

  check: (senseId: number, body: { answer: string }) =>
    request<{ verdict: SubmitResult['verdict'] }>(`/api/review/${senseId}/check`, {
      method: 'POST',
      body: JSON.stringify(body),
    }),

  known: (senseId: number, body: { answer: string; durationMs?: number; attemptId?: string }) =>
    request<{ known: boolean; lemma: string; result?: SubmitResult }>(
      `/api/review/${senseId}/known`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  letter: (senseId: number, count: number) =>
    request<{ letters: string; complete: boolean }>(`/api/review/${senseId}/letter`, {
      method: 'POST',
      body: JSON.stringify({ count }),
    }),
};
